/**
 * 入札中の札の一括取下 (AC-BSP-116) の確定の時点の結果を、 結果のバナーの内容にする。
 *
 * 全件を受け付けた時は何も出さない (受け付けた札の成立・取下失敗はこの後に分かるので、 ここでは
 * 知らせない)。 受け付けなかった札は、 札ごとの文言を出さず、 理由ごとに通常入札の札の数とブロック
 * 入札のグループ数を数える (backend の理由で分ける):
 * - 入札締切を過ぎていた (GATE_CLOSURE。 AC-BSP-057c): どの札も受け付けていない
 * - 状態が進んでいた (NOT_WITHDRAWABLE・NOT_FOUND): 取消・取下処理中・確定済ワークフロー経由 等で、
 *   やり直しても取り下げられない。 backend は、 申請セットの更新日時が変わっていても状態が進んでいた札を
 *   こちらにする
 * - 内容だけ先に更新されていた (UPDATED_BY_OTHER。 AC-BSP-124): 読み直せば取り下げ直せる
 *
 * RPC 自体が失敗した側は、 通信の失敗では backend が commit 済みでも応答が届かないことがあるので、
 * 取り下げたとも取り下げなかったとも言わず、 「結果を確認できなかった」 として分けて示す。 結果が分からない
 * reject には理由文を添えない (`ui-message-guide.md`「外部へ送る操作の結果を確かめられない時」: 再操作を
 * 促すと、 実際は受け付けられていた時に二重に送るおそれがある)。 応答に結果が無い札・理由が分からない札も、
 * 受け付けたとも受け付けなかったとも言えないので、 結果が分からない札に数える。
 */
import { Code, ConnectError } from '@connectrpc/connect'
import { BiddingBulkWithdrawFailureReason } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type {
  BiddingBulkWithdrawAccepted,
  BiddingBulkWithdrawFailure,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { TFunction } from 'i18next'

import { translateBlockWithdrawError } from '~/pages/SpotBidding/models/blockBidWithdrawAction'
import type { BulkWithdrawRequest } from '~/pages/SpotBidding/models/bulkWithdrawTargets'
import { translateSpotWriteError } from '~/pages/SpotBidding/models/spotBidWrite'

export type BulkWithdrawKind = 'spot' | 'block'

/** 応答の札 (ブロック入札はグループ) ごとの結果。 通常入札・ブロック入札の応答で同じ形。 */
type BulkWithdrawItemResult = {
  id: string
  outcome:
    | { case: 'accepted'; value: BiddingBulkWithdrawAccepted }
    | { case: 'failure'; value: BiddingBulkWithdrawFailure }
    | { case: undefined; value?: undefined }
}

/** 1 本の一括取下 RPC の結果。 */
export type BulkWithdrawSettled = PromiseSettledResult<{
  results: readonly BulkWithdrawItemResult[]
}>

/** 通常入札の札の数とブロック入札のグループ数。 */
export type BulkWithdrawCounts = { normal: number; block: number }

/** 確定の時点で受け付けなかった札 (AC-BSP-116)。 */
export type BulkWithdrawRefusals = {
  /** 入札締切を過ぎていて受け付けなかった札があったか。 */
  deadlinePassed: boolean
  /** 状態が進んでいて受け付けなかった札。 */
  advanced: BulkWithdrawCounts
  /** 内容だけ先に更新されていて受け付けなかった札。 */
  changed: BulkWithdrawCounts
}

/** 取り下げたかどうかが分からない札。 同じ理由の文は 1 つにまとめる。 */
export type BulkWithdrawUnconfirmed = {
  count: number
  /** 理由の文。 通信の失敗など、 結果が分からない reject には添えない (`undefined`)。 */
  message: string | undefined
}

export type BulkWithdrawOutcome = {
  /** 受け付けなかった札。 無ければ `undefined`。 */
  refusals: BulkWithdrawRefusals | undefined
  unconfirmed: BulkWithdrawUnconfirmed[]
  /** 結果が分かった側で取下を受け付けた札の数 (ブロック入札はグループ数)。 */
  acceptedCount: number
}

const ZERO: BulkWithdrawCounts = { normal: 0, block: 0 }

const hasAny = (c: BulkWithdrawCounts): boolean => c.normal + c.block > 0

/**
 * reject の原因が、 backend が処理したかどうか分からないものか。 通信の失敗・タイムアウト・中断・
 * 分類できない失敗 (`ConnectError` でない例外を含む) は、 backend が commit 済みでも応答が届かない
 * ことがある。 業務上の拒否 (FailedPrecondition・InvalidArgument・NotFound 等) は、 backend が
 * 受け付けなかったことを返している。
 */
const isOutcomeUnknown = (err: unknown): boolean =>
  !(err instanceof ConnectError) ||
  [
    Code.Unavailable,
    Code.DeadlineExceeded,
    Code.Unknown,
    Code.Canceled,
  ].includes(err.code)

type SettledSummary = {
  acceptedCount: number
  deadlinePassed: boolean
  advanced: number
  changed: number
  unconfirmed: BulkWithdrawUnconfirmed | undefined
}

/**
 * 1 本の一括取下 RPC の結果を、 受け付けた数・理由ごとに受け付けなかった数・結果が分からない数にする。
 *
 * - RPC が成功した: 応答の札ごとの結果を id で引く
 * - RPC が失敗した: 全対象を結果が分からないものとして数える。 理由は、 業務上の拒否の時だけ書き込み
 *   エラーの翻訳 (通常入札は [[translateSpotWriteError]]、 ブロック入札は [[translateBlockWithdrawError]])
 *   を添え、 通信の失敗などでは添えない
 * - 対象が無く RPC を呼ばなかった: 何も無い
 */
const summarizeSettled = (
  kind: BulkWithdrawKind,
  targetIds: readonly string[],
  settled: BulkWithdrawSettled,
  t: TFunction,
): SettledSummary => {
  const summary: SettledSummary = {
    acceptedCount: 0,
    deadlinePassed: false,
    advanced: 0,
    changed: 0,
    unconfirmed: undefined,
  }
  if (targetIds.length === 0) return summary
  if (settled.status === 'rejected') {
    const message = isOutcomeUnknown(settled.reason)
      ? undefined
      : kind === 'spot'
        ? translateSpotWriteError(settled.reason, t)
        : translateBlockWithdrawError(settled.reason, t)
    return { ...summary, unconfirmed: { message, count: targetIds.length } }
  }
  const resultById = new Map(settled.value.results.map((r) => [r.id, r]))
  let unknown = 0
  for (const id of targetIds) {
    const outcome = resultById.get(id)?.outcome
    if (outcome?.case === 'accepted') {
      summary.acceptedCount += 1
      continue
    }
    switch (outcome?.case === 'failure' ? outcome.value.reason : undefined) {
      case BiddingBulkWithdrawFailureReason.GATE_CLOSURE:
        summary.deadlinePassed = true
        break
      case BiddingBulkWithdrawFailureReason.NOT_WITHDRAWABLE:
      case BiddingBulkWithdrawFailureReason.NOT_FOUND:
        summary.advanced += 1
        break
      case BiddingBulkWithdrawFailureReason.UPDATED_BY_OTHER:
        summary.changed += 1
        break
      default:
        unknown += 1
    }
  }
  if (unknown > 0) summary.unconfirmed = { message: undefined, count: unknown }
  return summary
}

/** 通常入札とブロック入札で同じ文の理由は 1 つにまとめ、 件数を足す。 */
const mergeUnconfirmed = (
  items: ReadonlyArray<BulkWithdrawUnconfirmed | undefined>,
): BulkWithdrawUnconfirmed[] => {
  const counts = new Map<string | undefined, number>()
  for (const item of items) {
    if (item === undefined) continue
    counts.set(item.message, (counts.get(item.message) ?? 0) + item.count)
  }
  return [...counts].map(([message, count]) => ({ message, count }))
}

/**
 * 通常入札・ブロック入札の RPC の結果を合算して、 結果のバナーの内容にする。 全件を受け付けた時と、
 * 対象が無かった時は `undefined` (何も出さない)。
 */
export const summarizeBulkWithdraw = ({
  request,
  spot,
  block,
  t,
}: {
  request: BulkWithdrawRequest
  spot: BulkWithdrawSettled
  block: BulkWithdrawSettled
  t: TFunction
}): BulkWithdrawOutcome | undefined => {
  const s = summarizeSettled(
    'spot',
    request.spot.map((target) => target.id),
    spot,
    t,
  )
  const b = summarizeSettled(
    'block',
    request.block.map((target) => target.id),
    block,
    t,
  )
  const advanced = { normal: s.advanced, block: b.advanced }
  const changed = { normal: s.changed, block: b.changed }
  const deadlinePassed = s.deadlinePassed || b.deadlinePassed
  const refusals =
    deadlinePassed || hasAny(advanced) || hasAny(changed)
      ? { deadlinePassed, advanced, changed }
      : undefined
  const unconfirmed = mergeUnconfirmed([s.unconfirmed, b.unconfirmed])
  if (refusals === undefined && unconfirmed.length === 0) return undefined
  return {
    refusals,
    unconfirmed,
    acceptedCount: s.acceptedCount + b.acceptedCount,
  }
}

/**
 * 結果を出す時に、 一覧を自動で読み直すか。 内容だけ先に更新されていた札がある時は読み直さず、
 * 利用者の「再読み込み」 1 回で両方の一覧を読み直す (AC-BSP-116: 自動で読み直すと、 その札に出す
 * 「再読み込みしてからやり直してください」 の文とボタンが、 済んだ操作を求めることになるため)。
 * ただし結果が分からない札がある時は、 そのバナーが「一覧を読み直しました」 と言うので読み直す。
 */
export const shouldReloadListsAutomatically = (
  outcome: BulkWithdrawOutcome | undefined,
): boolean =>
  outcome === undefined ||
  outcome.unconfirmed.length > 0 ||
  !hasAny(outcome.refusals?.changed ?? ZERO)

/** 結果のバナーに「再読み込み」 を置くか (内容だけ先に更新されていた札がある時)。 */
export const offersReload = (outcome: BulkWithdrawOutcome): boolean =>
  hasAny(outcome.refusals?.changed ?? ZERO)
